import React, { useState } from 'react';

const RULES = [
  {
    key: 'loss_guard', name: 'Loss Guard',
    meaning: 'Request an exit when a completed daily close falls this far below the virtual entry fill price.',
  },
  {
    key: 'profit_target', name: 'Profit Target',
    meaning: 'Request an exit when a completed daily close rises this far above the virtual entry fill price.',
  },
  {
    key: 'profit_trail', name: 'Profit Trail',
    meaning: 'Request an exit when a completed daily close falls this far below the highest verified completed close since entry.',
  },
];

const validPercent = (value) => /^\d+(?:\.\d{1,2})?$/.test(String(value))
  && Number(value) >= 0.01 && Number(value) <= 99.99;

export default function ProtectiveStrategySetup({ client, sourceName, sourceFactorySlug, sourceDefinitionId, onSaved }) {
  const [name, setName] = useState(`${sourceName} with protective exits`);
  const [selected, setSelected] = useState({});
  const [values, setValues] = useState({ loss_guard: '5', profit_target: '8', profit_trail: '5' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedName, setSavedName] = useState('');
  const activeRules = RULES.filter((rule) => selected[rule.key]);
  const valid = activeRules.length > 0 && activeRules.every((rule) => validPercent(values[rule.key]));

  const save = async () => {
    if (saving || !valid) return;
    setSaving(true);
    setError('');
    try {
      const response = await client.post('/api/strategy-definitions/protective-composition', {
        strategy_name: name.trim(),
        source_factory_slug: sourceFactorySlug || null,
        source_definition_id: sourceDefinitionId || null,
        ...Object.fromEntries(RULES.map((rule) => [rule.key, selected[rule.key] ? values[rule.key] : null])),
      });
      setSavedName(response.data?.strategy_name || name.trim());
      onSaved(response.data);
    } catch (reason) {
      setError(reason.response?.data?.detail?.message || 'These protective rules could not be saved. Review the percentages and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30" aria-label="Optional protective exits">
      <h4 className="font-black">Add optional exit strategies</h4>
      <p className="mt-2 text-xs leading-5">Your entry strategy remains {sourceName}. These rules never open a position. They check completed daily closes and request Practice approval for a possible exit at the next eligible open; the fill price is not guaranteed.</p>
      <div className="mt-4 space-y-3">
        {RULES.map((rule) => (
          <div key={rule.key} className="rounded-xl border border-emerald-200 bg-white p-3 dark:border-emerald-800 dark:bg-slate-900">
            <label className="flex min-h-11 cursor-pointer items-center gap-2 font-bold">
              <input type="checkbox" checked={Boolean(selected[rule.key])} onChange={(event) => { setSelected((old) => ({ ...old, [rule.key]: event.target.checked })); setSavedName(''); }} />
              {rule.name}
            </label>
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">{rule.meaning}</p>
            {selected[rule.key] && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label htmlFor={`${rule.key}-slider`} className="text-sm font-bold">{rule.name}: {values[rule.key]}%</label>
                <input id={`${rule.key}-slider`} type="range" min="0.01" max="99.99" step="0.01" value={values[rule.key]} onChange={(event) => { setValues((old) => ({ ...old, [rule.key]: event.target.value })); setSavedName(''); }} className="min-w-32 flex-1" />
                <input aria-label={`${rule.name} exact percentage`} type="number" min="0.01" max="99.99" step="0.01" value={values[rule.key]} onChange={(event) => { setValues((old) => ({ ...old, [rule.key]: event.target.value })); setSavedName(''); }} className="w-24 rounded-lg border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-950" />
              </div>
            )}
          </div>
        ))}
      </div>
      {activeRules.length > 0 && (
        <div className="mt-4">
          <label htmlFor="protective-strategy-name" className="block text-sm font-bold">Name this combined strategy</label>
          <input id="protective-strategy-name" value={name} onChange={(event) => setName(event.target.value)} minLength={3} maxLength={100} className="mt-2 w-full rounded-lg border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-950" />
          <button type="button" disabled={!valid || name.trim().length < 3 || saving} onClick={save} className="mt-3 min-h-11 rounded-lg bg-emerald-700 px-4 py-2 font-bold text-white disabled:opacity-50">{saving ? 'Saving rules…' : 'Save combined strategy for this bot'}</button>
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-sm text-rose-700 dark:text-rose-300">{error}</p>}
      {savedName && <p role="status" className="mt-3 text-sm font-bold text-emerald-900 dark:text-emerald-200">Saved {savedName}. It is selected for your new Draft bot.</p>}
    </section>
  );
}
